/* eslint-disable complexity */
import settings from 'Cloud/settings';
import { APP_TITLE, IS_B2B_BIZ_USER, IS_REACT_PROMO_QUOTA_PAGE } from 'reactApp/appHelpers/configHelpers';
import { ERROR_STATUS_MAP } from 'reactApp/sections/ErrorPage/ErrorPage.helpers';
import { EStatus } from 'reactApp/sections/ErrorPage/ErrorPage.types';

import { type CloudFile, type CloudItem, EStorageType, INTEGRATION_STORAGES } from './storage.types';

// eslint-disable-next-line max-lines-per-function
export const getStorage = (storage: string | null) => {
    const isHome = storage === EStorageType.home;
    const isPublic = storage === EStorageType.public;
    const isSharedAutodelete = storage === EStorageType.sharedAutodelete;
    const isSharedLinks = storage === EStorageType.sharedLinks;
    const isSharedIncoming = storage === EStorageType.sharedIncoming;
    const isTrashBin = storage === EStorageType.trashbin;
    const isAttaches = storage === EStorageType.attaches || storage === EStorageType.viewerAttaches;
    const isSearch = storage === EStorageType.search;
    const isFavorites = storage === EStorageType.favorites;
    const isStock = storage === EStorageType.stock;
    const isFeed = storage === EStorageType.feed;
    const isStart = storage === EStorageType.start;
    const isGallery = storage === EStorageType.gallery;
    const isRecommend = storage === EStorageType.recommend;
    const isDocuments = storage === EStorageType.documents;
    const isStory = storage === EStorageType.story;
    const isEditor =
        storage === EStorageType.r7 ||
        storage === EStorageType.myoffice ||
        storage === EStorageType.r7wopi ||
        storage === EStorageType.myofficeAttaches;
    const isAttachesStorage = storage === EStorageType.attaches;
    const isAlbums = storage === EStorageType.albums;
    const isQuotaCleaner = storage === EStorageType.quotaCleaner;
    const isQuotaPartner = storage === EStorageType.quotaPartner;
    const isQuotaTariffs = storage === EStorageType.quotaTariffs;
    const isSubscription = storage === EStorageType.subscriptions;
    const isEmbedded = storage === EStorageType.embedded;
    const isFamilyPage = storage === EStorageType.family;
    const isAllDocuments = storage === EStorageType.alldocuments;
    const isPdfEdit = storage === EStorageType.pdfEdit;
    const isIntegration = storage === EStorageType.integration;
    const isInlineIntegration = storage === EStorageType.inlineIntegration;
    const isTemplates = storage === EStorageType.templates;
    const isSubscriptions = storage === EStorageType.subscriptions;
    const isIncomingPublic = storage === EStorageType.incomingPublic;
    const isFamilyFolders = storage === EStorageType.familyFolders;
    const isFamilyAlbums = storage === EStorageType.familyAlbums;
    const isFamilyLanding = storage === EStorageType.familyLanding;
    const isVigbo = storage === EStorageType.vigbo;

    return {
        isHome,
        isPublic,
        isSharedAutodelete,
        isSharedLinks,
        isSharedIncoming,
        isTrashBin,
        isAttaches,
        isSearch,
        isFavorites,
        isStock,
        isFeed,
        isStart,
        isGallery,
        isRecommend,
        isDocuments,
        isStory,
        isEditor,
        isAttachesStorage,
        isAlbums,
        isQuotaCleaner,
        isQuotaTariffs,
        isQuotaPartner,
        isEmbedded,
        isAllDocuments,
        isPdfEdit,
        isFamilyPage,
        isTemplates,
        isIncomingPublic,
        isPromoQuota: isQuotaCleaner || isQuotaPartner || isQuotaTariffs,
        isPrivatePage:
            isHome ||
            isGallery ||
            isFavorites ||
            isDocuments ||
            isAllDocuments ||
            isSharedAutodelete ||
            isStart ||
            isAlbums ||
            isDocuments ||
            isTrashBin ||
            isFeed ||
            isRecommend ||
            isSharedIncoming ||
            isSharedLinks ||
            isFamilyPage ||
            isAttachesStorage ||
            isIncomingPublic ||
            isVigbo,
        isPublicOrStock: isPublic || isStock,
        isNewQuotaLanding: isQuotaPartner || isQuotaTariffs,
        isSubscription,
        isIntegration,
        isInlineIntegration,
        isSubscriptions,
        isFamilyFolders,
        isFamilyAlbums,
        isFamilyLanding,
        isVigbo,
    };
};

export const getPageSubtitle = () => {
    if (IS_REACT_PROMO_QUOTA_PAGE) {
        return 'Единое хранилище Почты и Облака';
    }
    return IS_B2B_BIZ_USER ? APP_TITLE : 'Облако Mail';
};

export const getPageTitleByStorage = (storage, itemTitle): string => {
    const title = itemTitle ? itemTitle : settings?.storages?.[storage]?.title;
    const subTitle = getPageSubtitle();

    if (title === ERROR_STATUS_MAP[EStatus.BAD_BROWSER].title) {
        return subTitle;
    }

    return title ? `${title} / ${subTitle}` : subTitle;
};

export const DEFAULT_API_LIMIT = 500;

export const getLoadMoreLimit = (storage: EStorageType): number => {
    if ([EStorageType.home, EStorageType.public, EStorageType.sharedLinks, EStorageType.sharedIncoming].includes(storage)) {
        return DEFAULT_API_LIMIT;
    }

    if (storage === EStorageType.favorites) {
        return 1000;
    }

    if (storage === EStorageType.search) {
        return 10000;
    }

    return 100;
};

export const isCloudFile = (item?: CloudItem | null): item is CloudFile => !!item && !item.isFolder;

export const getIdByStorage = (storage: EStorageType) => {
    return `/${storage}`;
};

export const hasStorageDatalist = (storage: EStorageType) =>
    [
        EStorageType.albums,
        EStorageType.alldocuments,
        EStorageType.attaches,
        EStorageType.favorites,
        EStorageType.feed,
        EStorageType.gallery,
        EStorageType.home,
        EStorageType.integration,
        EStorageType.inlineIntegration,
        EStorageType.documents,
        EStorageType.search,
        EStorageType.sharedAutodelete,
        EStorageType.sharedIncoming,
        EStorageType.sharedLinks,
        EStorageType.trashbin,
        EStorageType.public,
        EStorageType.stock,
        EStorageType.start,
        EStorageType.vigbo,
    ].includes(storage);

export const isIntegrationStorage = (storage: EStorageType | undefined | null) => Boolean(storage && INTEGRATION_STORAGES[storage]);

export const isEditorStorage = (storage: EStorageType): boolean =>
    [EStorageType.editor, EStorageType.r7, EStorageType.myoffice, EStorageType.myofficeAttaches, EStorageType.r7wopi].includes(storage);
